<!-- 应急事件时间轴 -->
<template>
  <div class="emergency-event-timeline nx-page">
    <div class="page-header">
      <h2>应急事件时间轴</h2>
    </div>

    <div class="toolbar card-block">
      <span class="label">选择事件</span>
      <el-select
        v-model="eventId"
        class="event-select"
        filterable
        remote
        clearable
        reserve-keyword
        placeholder="输入事件名称或编号搜索"
        :remote-method="remoteSearch"
        :loading="eventLoading"
        @change="onEventChange"
      >
        <el-option
          v-for="item in eventOptions"
          :key="String(item.id)"
          :label="formatOptionLabel(item)"
          :value="item.id"
        />
      </el-select>
      <el-button type="primary" :loading="detailLoading" @click="loadDetail">刷新</el-button>
    </div>

    <div v-if="summary.eventNumber" class="summary-card card-block">
      <div class="row1">
        <span class="event-no">{{ summary.eventNumber }}</span>
        <h3 class="title">{{ summary.eventName }}</h3>
        <el-tag v-if="summary.urgencyLabel" type="danger" effect="plain" size="small">{{ summary.urgencyLabel }}</el-tag>
        <el-tag v-if="summary.eventStatusLabel" type="primary" effect="plain" size="small">{{ summary.eventStatusLabel }}</el-tag>
      </div>
      <div class="meta-grid">
        <div><span class="k">事件类型</span>{{ summary.eventTypeLabel }}</div>
        <div><span class="k">发生时间</span>{{ summary.occurTime || '—' }}</div>
        <div><span class="k">上报时间</span>{{ summary.reportTime || '—' }}</div>
        <div><span class="k">位置</span>{{ summary.location || '—' }}</div>
        <div><span class="k">关联预案</span>{{ summary.planName }}</div>
        <div><span class="k">响应等级</span>{{ summary.responseLevelLabel || '—' }}</div>
        <div><span class="k">上报人</span>{{ summary.reporterName || '—' }}</div>
      </div>
    </div>

    <div class="legend card-block">
      <span><i class="dot event" />事件</span>
      <span><i class="dot plan" />预案</span>
      <span><i class="dot task" />派工/工单</span>
      <span><i class="dot state" />状态</span>
      <span><i class="dot end" />结束</span>
    </div>

    <div v-loading="detailLoading" class="timeline-wrap card-block">
      <template v-if="!eventId">
        <el-empty description="请选择应急事件" />
      </template>
      <template v-else-if="!detailLoading && timelineItems.length === 0">
        <el-empty description="暂无时间轴节点（事件无时间字段或未返回详情）" />
      </template>
      <div v-else class="timeline">
        <div v-for="(it, idx) in timelineItems" :key="idx" class="tl-item">
          <div :class="['tl-dot', it.dot]" />
          <div class="tl-time">{{ it.timeStr }}</div>
          <div class="tl-card">
            <div class="tl-title">{{ it.title }}</div>
            <p class="tl-body">{{ it.body }}</p>
            <div v-if="it.meta" class="tl-meta">{{ it.meta }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getEventDetail, getEventPage } from './api';
import { buildTimeline, summaryFromDetail } from './buildTimeline';

export default {
  name: 'EmergencyEventTimeline',
  data() {
    return {
      eventId: null,
      eventOptions: [],
      eventLoading: false,
      detailLoading: false,
      detail: null,
      timelineItems: [],
      summary: {}
    };
  },
  mounted() {
    this.remoteSearch('');
  },
  methods: {
    formatOptionLabel(row) {
      return `${row.eventNumber || ''} ${row.eventName || ''}`.trim() || String(row.id);
    },
    async remoteSearch(query) {
      this.eventLoading = true;
      try {
        const q = (query || '').trim();
        const params = { page: 1, size: 40 };
        if (q) {
          if (/YJSJ|^\d/.test(q)) params.eventNumber = q;
          else params.eventName = q;
        }
        const res = await getEventPage(params);
        const page = res.data && res.data.data;
        this.eventOptions = (page && page.records) || [];
      } catch (e) {
        console.error(e);
        this.eventOptions = [];
      } finally {
        this.eventLoading = false;
      }
    },
    onEventChange() {
      this.loadDetail();
    },
    async loadDetail() {
      if (!this.eventId) {
        this.detail = null;
        this.timelineItems = [];
        this.summary = {};
        return;
      }
      this.detailLoading = true;
      try {
        const res = await getEventDetail(this.eventId);
        const data = res.data && res.data.data;
        this.detail = data;
        this.timelineItems = buildTimeline(data);
        this.summary = summaryFromDetail(data);
        const row = this.eventOptions.find((x) => String(x.id) === String(this.eventId));
        if (row && data) {
          row.eventNumber = data.eventNumber || row.eventNumber;
          row.eventName = data.eventName || row.eventName;
        }
      } catch (e) {
        console.error(e);
        this.$message.error('加载事件详情失败');
        this.detail = null;
        this.timelineItems = [];
        this.summary = {};
      } finally {
        this.detailLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.emergency-event-timeline {
  padding: 16px 20px 32px;
  background: #f1f5f9;
  min-height: calc(100vh - 120px);
}
.page-header {
  margin-bottom: 16px;
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0f172a;
  }
}
.card-block {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
  .label {
    font-size: 13px;
    color: #475569;
  }
  .event-select {
    min-width: 320px;
    flex: 1;
    max-width: 520px;
  }
}
.summary-card {
  padding: 16px 18px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fff 55%, #f8fafc 100%);
  border-color: #fecaca;
  .row1 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .event-no {
    font-family: ui-monospace, monospace;
    font-size: 12px;
    color: #64748b;
    background: #fff;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }
  .title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: #991b1b;
    flex: 1;
    min-width: 200px;
  }
  .meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px 16px;
    font-size: 13px;
    color: #334155;
    .k {
      color: #94a3b8;
      margin-right: 6px;
    }
  }
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 10px 18px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #64748b;
  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    &.event { background: #dc2626; }
    &.plan { background: #2563eb; }
    &.task { background: #059669; }
    &.state { background: #d97706; }
    &.end { background: #475569; }
  }
}
.timeline-wrap {
  padding: 24px 20px 28px;
  min-height: 200px;
}
.timeline {
  position: relative;
  padding-left: 28px;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 6px;
  bottom: 6px;
  width: 3px;
  background: linear-gradient(180deg, #fecaca, #cbd5e1);
  border-radius: 2px;
}
.tl-item {
  position: relative;
  padding-bottom: 22px;
}
.tl-item:last-child {
  padding-bottom: 0;
}
.tl-dot {
  position: absolute;
  left: -22px;
  top: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 3px solid #fff;
}
.tl-dot.event {
  background: #dc2626;
  box-shadow: 0 0 0 2px #fecaca;
}
.tl-dot.plan {
  background: #2563eb;
  box-shadow: 0 0 0 2px #bfdbfe;
}
.tl-dot.task {
  background: #059669;
  box-shadow: 0 0 0 2px #a7f3d0;
}
.tl-dot.state {
  background: #d97706;
  box-shadow: 0 0 0 2px #fde68a;
}
.tl-dot.end {
  background: #475569;
  box-shadow: 0 0 0 2px #cbd5e1;
}
.tl-time {
  font-size: 12px;
  font-family: ui-monospace, monospace;
  color: #64748b;
  margin-bottom: 6px;
}
.tl-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
}
.tl-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
}
.tl-body {
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.55;
}
.tl-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}
</style>
